/**
 * English UI catalogue of the Song & Timing workspace (W2-08 timing), namespace 'timing'. zh-CN lives in
 * src/i18n/zh-CN/timing.ts (localisation package W3-09). Wording follows DESIGN.md §4.15 and §5.2: timing is two
 * questions the user can hear ("how fast is the beat?" and "where is the first beat?").
 */

import { catalogOf, defineMessages } from '../../i18n/define';

export const m = defineMessages('timing', {
  // ---- panels ----
  panelLeft: 'Tempo and first beat',
  panelMain: 'Song overview',
  panelBottom: 'Check by ear',
  noProject: 'Open a project to set its tempo and first beat.',
  noSongTitle: 'This chart has no song yet',
  noSongBody: 'Add a song file to the project to set its tempo and first beat.',

  // ---- analysis status ----
  analysing: 'Analysing the song',
  analysingTask:
    '{task, select, decode {Decoding the song} peaks {Reading the waveform} onsets {Finding the beats} tempo {Detecting the tempo} offset {Finding the first beat} other {Analysing the song}}',
  waitingForAudio: 'Waiting for the song to finish loading',
  loadSong: 'Load the song',
  analysisFailed: 'The song could not be analysed: {error}',
  notDecoded:
    'This song plays straight from the file (it is very long, or the browser could not decode it), so it cannot be analysed here. Set the tempo by tapping and check the first beat by ear.',
  noDecoder:
    'This browser cannot decode the song for the analysis. Set the tempo by tapping and check the first beat by ear.',
  decodeFailed:
    'The browser could not decode this song file for the analysis. Try converting it to MP3 or OGG, or set the tempo by tapping and check the first beat by ear.',
  retry: 'Try again',

  // ---- confirmed status ----
  confirmedYes: 'Timing confirmed',
  confirmedNo: 'Timing not confirmed yet',
  confirmedNoHelp: 'Listen with the clicks below. When they land on the beat, confirm the timing.',
  confirmAction: 'Confirm timing',
  unconfirmAction: 'Mark as not confirmed',

  // ---- tempo ----
  sectionTempo: 'Tempo',
  bpmLabel: 'Song tempo (BPM)',
  bpmHelp: 'Arrow keys change it by 0.1, with Ctrl by 0.01, with Shift by 1.',
  bpmInvalid: 'A tempo is a number greater than 0, like 174 or 128.5.',
  bpmDown: 'Slower by 0.1 BPM (Ctrl 0.01, Shift 1)',
  bpmUp: 'Faster by 0.1 BPM (Ctrl 0.01, Shift 1)',
  bpmMultiple: 'This chart changes tempo. The field sets the first tempo; edit the others in the table.',
  detect: 'Detect',
  detectTempoTip: 'Find the tempo of the song automatically',
  detecting: 'Detecting',
  detectAgain: 'Detect again',
  candidatesLabel: 'Tempo candidates',
  candidate: '{bpm, number, 0.0} BPM',
  strength: '{level, select, strong {strong} medium {medium} other {weak}}',
  strengthTip: 'How well the beats of this tempo match the song ({pct, number, integer} %)',
  useCandidate: 'Use',
  useCandidateLabel: 'Use {bpm, number, 0.0} BPM',
  inUse: 'In use',
  noCandidates: 'No clear beat was found. Tap the tempo instead.',
  octaveHelp: 'Detection can pick half or double the real tempo. Fix it here:',
  times2: 'x2',
  div2: '/2',
  times15: 'x1.5',
  times2Label: 'Double the tempo',
  div2Label: 'Halve the tempo',
  times15Label: 'Multiply the tempo by 1.5',
  div15Label: 'Divide the tempo by 1.5',

  // ---- tap ----
  tap: 'Tap',
  tapTip: 'Tap along with the beat (key B)',
  tapIdle: 'Tap at least 4 times along with the beat.',
  tapCounting: '{count, plural, one {# tap} other {# taps}}: keep going',
  tapResult: '{count, plural, one {# tap} other {# taps}}: {bpm, number, 0.0} BPM',
  tapSpread: 'spread +-{spread, number, 0.0}',
  tapUse: 'Use',
  tapUseLabel: 'Use the tapped tempo ({bpm, number, 0.0} BPM)',
  tapReset: 'Reset taps',
  tapResetHint: 'Taps reset after 2 seconds without a tap.',

  // ---- first beat ----
  sectionFirstBeat: 'First beat',
  beatZeroAt: 'Beat 0 is heard at {sec, number, 0.000} s',
  beatZeroHelp: 'The chart offset: where the first beat sits in the song.',
  offsetEarlier: 'Beat 0 earlier by 1 ms (Shift 10 ms)',
  offsetLater: 'Beat 0 later by 1 ms (Shift 10 ms)',
  offsetValueLabel: 'Offset in milliseconds',
  /** Unit after the offset field. */
  offsetUnit: 'ms',
  detectOffsetTip: 'Line the beat grid up with the song automatically',
  offsetConfidence: 'Confidence {pct, number, integer} %',
  offsetDetected: 'Beat 0 moved to {sec, number, 0.000} s (confidence {pct, number, integer} %).',
  offsetLowConfidence:
    'Beat 0 moved to {sec, number, 0.000} s, but the confidence is low ({pct, number, integer} %). Check it by ear.',
  offsetNotFound: 'The first beat could not be found in this song. Click a downbeat in the overview instead.',
  phiraAdjust: 'Phira per-chart adjustment',
  phiraAdjustValue: '{sign}{sec, number, 0.000} s',
  phiraAdjustHelp:
    'This package had its own offset in info.yml. Phira adds it on top of the chart offset. Folding it in keeps the timing and removes the extra setting.',
  foldIn: 'Fold into chart offset',
  folded: 'The per-chart adjustment is now part of the chart offset.',

  // ---- tempo table ----
  sectionTempoChanges: 'Tempo changes',
  colBeat: 'Beat',
  colBpm: 'BPM',
  colMeter: 'Beats per bar',
  colActions: 'Actions',
  rowPinned: 'The first tempo point is fixed at beat 0. Only its tempo can change.',
  bpmRowInheritedLabel: 'Tempo at beat {beat} (same as before)',
  meterInheritedLabel: 'Beats per bar at beat {beat} (same as before)',
  deleteRow: 'Delete this change',
  deleteRowLabel: 'Delete the change at beat {beat}',
  meterLabel: 'Beats per bar at beat {beat}',
  bpmRowLabel: 'Tempo at beat {beat}',
  beatRowLabel: 'Beat of the change',
  addAtPlayhead: 'Add at playhead',
  addAtPlayheadTip: 'Add a tempo change at the playhead with the current tempo',
  addMeter: 'Meter change',
  addMeterTip: 'Change the beats per bar from the playhead on',
  insertBeats: 'Insert beats',
  removeRange: 'Remove range',
  insertAt: 'At beat',
  insertLength: 'Beats to insert',
  insertGo: 'Insert',
  inserted: 'Inserted {beats} beats; {count, plural, one {# item} other {# items}} moved later.',
  removeFrom: 'From beat',
  removeTo: 'To beat',
  removeGo: 'Remove',
  removed:
    'Removed the range: {removed, plural, one {# note} other {# notes}} deleted, {moved, plural, one {# item} other {# items}} moved earlier.',
  rangeInvalid: 'The end must come after the start.',
  beatInvalid: 'That is not a beat. Type one like 16, 16:1/2, 16.5 or 23.5s (a time in seconds).',
  meterInvalid: 'Beats per bar is a number above 0 and up to 64, like 4, 3 or 3.5 (for 7/8).',
  policyLabel: 'When a tempo changes',
  policyKeepBeats: 'Notes keep their beats',
  policyKeepTime: 'Notes keep their time',
  /** Short choices of the tempo-policy switch (the full sentence is the help line under it). */
  policyKeepBeatsShort: 'Keep beats',
  policyKeepTimeShort: 'Keep time',
  policyKeepBeatsHelp: 'Like RPE: later notes stay on their beats and move in time.',
  policyKeepTimeHelp: 'Later notes stay where they sound; their beats are recalculated.',
  retimed:
    'Re-timed {retimed, plural, one {# item} other {# items}}{snapped, plural, =0 {} one {, # snapped to 1/1920 beat} other {, # snapped to 1/1920 beat}}.',
  meterExists: 'There is already a meter change at beat {beat}.',
  otherDifficulties:
    '{count, plural, one {The other difficulty of this song still uses the old tempo map.} other {The # other difficulties of this song still use the old tempo map.}}',
  meterBefore: 'Meter changes start a new bar: bar lines, the metronome accent and the bar-by-bar view follow them.',

  // ---- metronome ----
  sectionMetronome: 'Metronome',
  metronomeOn: 'Metronome clicks while playing',
  metronomeVolume: 'Metronome volume',
  /** Metronome volume slider value (0..1 shown as a percentage). */
  volumePercent: '{value, number, percent}',
  metronomePreview: 'Play one click',

  // ---- song info ----
  sectionSongInfo: 'Song info',
  infoFile: 'File',
  infoDuration: 'Length',
  infoFormat: 'Format',
  infoSampleRate: 'Sample rate',
  infoChannels: 'Channels',
  infoSampleRateValue: '{khz, number, 0.0} kHz',
  infoChannelsValue: '{count, plural, one {mono} =2 {stereo} other {# channels}}',
  infoPhiraOk: 'Phira can play this',
  infoPhiraNo: 'Phira cannot play this format',
  infoTitle: 'Title',
  infoArtist: 'Artist',
  infoUnknown: 'Unknown',
  infoEdit: 'Edit song details',
  infoMp3: 'MP3 adds a small decoder delay that differs between players. OGG or WAV keeps timing exact.',
  infoStreaming: 'This song is long, so it plays from the file: the timing view is slightly less exact.',

  // ---- song overview ----
  overviewLabel: 'Song waveform with the beat grid',
  /** Screen readers announce this instead of "group" for the overview. */
  overviewRoleDescription: 'waveform',
  overviewHelp:
    'Click a downbeat to put beat 0 there, or drag the grid. The strip at the top seeks. Ctrl+wheel zooms, wheel scrolls.',
  overviewHover: '{time} · beat {beat}',
  overviewBeatZero: 'Beat 0',
  overviewZoomIn: 'Zoom in',
  overviewZoomOut: 'Zoom out',
  overviewZoomFit: 'Show the whole song',
  overviewSetBeatZero: 'Beat 0 moved to {sec, number, 0.000} s.',
  overviewDragged: 'Beat 0 is now at {sec, number, 0.000} s.',

  // ---- alignment ----
  alignmentTitle: 'Bar by bar',
  alignmentHelp:
    'One strip per bar. With the right tempo the beats form straight columns. Hover to listen, click to seek, Alt+wheel to nudge the tempo by 0.01.',
  alignmentLabel: 'Bar-by-bar alignment of the song',
  alignmentRows: 'Rows',
  alignmentRowsFit: 'Fit',
  alignmentLock: 'Lock region',
  alignmentLocked: 'Locked to bars {from} to {to}',
  alignmentUnlock: 'Unlock',
  alignmentFollow: 'Rows follow the playhead',
  alignmentScrub: 'Listen while hovering',
  alignmentBar: 'Bar {bar}',
  /** Readout under the pointer in the bar-by-bar view: bar and song time. */
  alignmentReadout: 'Bar {bar} · {time}',
  /** Left = too low, right = too high: the physics; DESIGN.md §4.15's diagram has it inverted (CR-W2-08-9). */
  alignmentDriftLeft: 'The columns lean left: the tempo may be a little too low.',
  alignmentDriftRight: 'The columns lean right: the tempo may be a little too high.',
  alignmentStraight: 'The columns are straight: the tempo matches.',
  alignmentNoData: 'No beats to compare yet.',
  alignmentPrev: 'Earlier bars',
  alignmentNext: 'Later bars',

  // ---- check by ear ----
  playWithClicks: 'Play with clicks',
  stopClicks: 'Stop',
  byEarQuestion: 'Does the click land on the beat?',
  tooEarly: 'Too early',
  justRight: 'Just right',
  tooLate: 'Too late',
  tooEarlyTip: 'The click comes before the beat: move beat 0 later by {ms} ms',
  tooLateTip: 'The click comes after the beat: move beat 0 earlier by {ms} ms',
  justRightTip: 'The clicks land on the beat: confirm the timing',
  byEarStep: 'Each press moves beat 0 by {ms} ms',
  byEarReset: 'Start again with 10 ms steps',
  byEarHeadphones: 'Adjusting by ear does not depend on your headphones.',
  byEarConfirmed: 'Timing confirmed. You can still fine-tune it at any time.',
  latencyLabel: 'Audio latency:',
  latencyValue: '{ms, number, integer} ms calibration',
  latencyNone: 'Not calibrated',
  calibrate: 'Calibrate',
  calibrateTip: 'Measure the delay of your speakers or headphones',

  // ---- latency calibration dialog ----
  calTitle: 'Calibrate audio latency',
  calIntro:
    'Speakers and especially wireless headphones play sound a little late. Tap along with the clicks so notes line up with what you hear. This changes only what you see, never the chart.',
  calHow: 'Press any key (or click here) on every click. The first 4 clicks are a count-in.',
  calStart: 'Start',
  calAgain: 'Measure again',
  calCountIn: 'Get ready: {n}',
  calTapping: 'Tap along: click {n} of {total}',
  calResult: 'Measured delay: {ms, number, integer} ms',
  calResultHelp: 'Your current calibration is {ms, number, integer} ms.',
  calSave: 'Use this value',
  calCancel: 'Cancel',
  calClose: 'Close',
  calDetected: 'The browser reports {ms, number, integer} ms of output delay.',
  calDetectedUnknown: 'The browser does not report the output delay of this device.',
  calSaved: 'Audio latency calibration set to {ms, number, integer} ms.',
  calFailed: 'Calibration failed: {error}',
  calResetValue: 'Reset to 0 ms',

  // ---- latency banner ----
  hintBanner: 'Your audio device delays sound by about {ms, number, integer} ms. Calibrate so notes line up.',
  hintBannerUnknown: 'Your browser does not report the delay of your audio device. Calibrate so notes line up.',
  hintCalibrate: 'Calibrate',
  hintDismiss: 'Dismiss',

  // ---- wizard beat step ----
  stepTitle: 'Beat',
  stepIntro: 'Two questions you can hear: how fast is the beat, and where is the first beat?',
  stepDetected: 'Detected {bpm, number, 0.0} BPM with beat 0 at {sec, number, 0.000} s. Listen and adjust.',
  stepDetecting: 'Detecting the tempo in the background',
  stepNoSong: 'Choose a song first.',
  stepSkipNote:
    'Skipping keeps {bpm, number, 0.##} BPM and beat 0 at {sec, number, 0.###} s without confirming them, and the chart reminds you to check the timing.',
  stepOffsetLabel: 'Beat 0 in milliseconds',
  stepClickHint: 'Click a downbeat in the waveform to put beat 0 there.',
  stepEngineBusy: 'Close the open chart to listen to this song here.',

  // ---- A/V sync lab (#/dev/av) ----
  labTitle: 'A/V sync lab',
  labBack: 'Back to the Library',
  labIntro:
    'Checks how well sound and picture line up on this device. Every measurement goes into the log at the bottom; save it and send it with your report.',
  labFlashTitle: 'Click and flash',
  labFlashHelp:
    'A metronome plays at 120 BPM and the square lights up on every beat. Film the screen and speaker in slow motion (or use a light sensor and a microphone): the gap between flash and click is the remaining delay. Compare both paths after calibrating.',
  labPath: 'Playback path',
  labPathWebAudio: 'Web Audio (1x)',
  labPathMedia: 'Media element (keep pitch, 0.75x)',
  labStart: 'Start',
  labStop: 'Stop',
  labBusy: 'Wait for the current measurement to finish.',
  labCloseChart: 'Close the open chart first: the lab uses the audio engine on its own.',
  labTapTitle: 'Tap along',
  labTapHelp:
    'After 4 count-in clicks, press any key on each of the next 16 clicks. The result is the delay you still hear with the current calibration.',
  labTapStart: 'Measure by tapping',
  labTapResult: 'Remaining delay: {ms, number, integer} ms',
  labDecodeTitle: 'Decoder check',
  labDecodeHelp:
    'Choose the same metronome in several formats (WAV, MP3, OGG, FLAC, M4A, Opus). The first click of each is located; with a WAV among them, the others are compared with it. An MP3 without a LAME tag starts about 25 ms late.',
  labDecodePick: 'Choose audio files',
  labColFile: 'File',
  labColOnset: 'First click (ms)',
  labColVsWav: 'Against the WAV (ms)',
  labColMp3: 'MP3 tag',
  labLogTitle: 'Log ({count, plural, one {# entry} other {# entries}})',
  labLogEmpty: 'Nothing measured yet.',
  labLogCopy: 'Copy log',
  labLogSave: 'Save log',
  labLogClear: 'Clear',
  labLogCopied: 'The log is on the clipboard.',

  // ---- commands ----
  cmdCalibrate: 'Calibrate audio latency',
  cmdDetectTempo: 'Detect tempo',
  cmdDetectOffset: 'Detect first beat',
  cmdTap: 'Tap tempo',
  cmdTapReset: 'Reset tap tempo',
  cmdTapUse: 'Use the tapped tempo',
  cmdAddTempo: 'Add tempo change at playhead',
  cmdAddMeter: 'Add meter change at playhead',
  cmdOffsetEarlier: 'Move beat 0 earlier by 1 ms',
  cmdOffsetLater: 'Move beat 0 later by 1 ms',
  cmdMultiply: 'Multiply the tempo',
  cmdMultiplyArg: 'Factor',
  cmdPlayClicks: 'Play with clicks',
  cmdTooEarly: 'The click is too early',
  cmdJustRight: 'The click is just right',
  cmdTooLate: 'The click is too late',
  cmdConfirm: 'Confirm timing',
  cmdFold: 'Fold Phira per-chart adjustment into the chart offset',
  cmdPolicy: 'When a tempo changes',
  cmdBeatZeroHere: 'Put beat 0 under the pointer',
  noProjectReason: 'Open a project first.',
  noSongReason: 'This chart has no song.',
  noTapReason: 'Tap at least 4 times first.',
  nothingToFold: 'There is no per-chart adjustment.',
  noBpmReason: 'There is no tempo to change.',

  // ---- transaction labels ----
  txBpm: 'Set tempo to {bpm, number, 0.00} BPM',
  txAddBpm: 'Add tempo change at beat {beat}',
  txDeleteBpm: 'Delete tempo change at beat {beat}',
  txMoveBpm: 'Move tempo change to beat {beat}',
  txOffset: 'Set beat 0 to {sec, number, 0.000} s',
  txFold: 'Fold per-chart adjustment',
  txConfirm: 'Confirm timing',
  txUnconfirm: 'Mark timing as not confirmed',
  txMeter: 'Set {beats} beats per bar at beat {beat}',
  txAddMeter: 'Add meter change at beat {beat}',
  txDeleteMeter: 'Delete meter change at beat {beat}',
  txInsert: 'Insert {beats} beats',
  txRemove: 'Remove beats {from} to {to}',

  // ---- settings rows ----
  setScrub: 'Listen while hovering the bar-by-bar view',
  setScrubDesc: 'Plays a short snippet of the song under the pointer.',
  setRows: 'Bars shown in the bar-by-bar view',
  setRowsDesc: '0 fits as many bars as the view has room for.',
  setAutoDetect: 'Detect the tempo when the Song & Timing workspace opens',
  setAutoDetectDesc: 'Runs once per song; results are kept with the project.',

  // ---- help ----
  helpTimingTitle: 'Set the tempo and the first beat',
  helpTimingBody:
    'Timing is two questions you can hear. **How fast is the beat?** Use Detect, tap along with B, or type the BPM; the bar-by-bar view shows straight columns when it is right. **Where is the first beat?** Click a downbeat in the overview or use Detect, then press Play with clicks and answer Too early, Just right or Too late until the clicks land on the beat.',
  helpLatencyTitle: 'Audio latency',
  helpLatencyBody:
    'Wireless headphones can delay sound by 150 to 300 ms. Calibrate the latency in Settings or in Song & Timing so what you see lines up with what you hear. Calibration never changes the chart; moving beat 0 does.',
  helpMeterTitle: 'Meter changes',
  helpMeterBody:
    'Each tempo change has a number of beats per bar, and you can change the meter without changing the tempo. Bar lines, Page Up / Page Down, the metronome accent and the bar-by-bar view follow the meter, so a 3/4 or 7/8 section stays correct.',
});

export default catalogOf(m);
